/**
 * ============================================================================
 * usePayoutBookings - PAYOUT BOOKINGS FETCHING HOOKS
 * ============================================================================
 *
 * Manages fetching of different booking categories for payout displays:
 * - Held Bookings: Bookings in holding period
 * - Eligible Bookings: Ready for payout
 * - Reserved Bookings: Future reserved balance
 *
 * Uses TanStack Query for caching to prevent repeated API calls
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';

export interface PayoutBooking {
  id: number;
  booking_id?: number;
  booking_reference: string;
  customer_name: string;
  sport_name?: string;
  court_name?: string;
  booking_date?: string;
  date?: string;
  time_slot?: string;
  start_time?: string;
  end_time?: string;
  total_amount?: number;
  venue_share: number | string; // Can be string from DB DECIMAL
  vendor_share?: number | string; // Alias for venue_share
  platform_share?: number | string;
  status?: string;
  payout_status?: string;
  payout_eligible_at?: string;
  booking_type?: string;
  releases_in?: string; // For held bookings
}

/**
 * Fetch held bookings (in holding period)
 *
 * Cached for 2 minutes to prevent loading on every modal open
 * Invalidated when payout is requested
 *
 * @example
 * ```typescript
 * const { data: heldBookings, isLoading } = useHeldBookings(venueId);
 *
 * if (isLoading) return <Spinner />;
 * return <BookingsList bookings={heldBookings} />;
 * ```
 */
export function useHeldBookings(venueId: number | undefined) {
  return useQuery<PayoutBooking[], Error>({
    queryKey: ['held-bookings', venueId],
    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');
      return vendorApi.getHeldBookings(venueId);
    },
    enabled: !!venueId,

    // Cache for 2 minutes
    staleTime: 2 * 60 * 1000,

    // Don't refetch on window focus (modal data is relatively static)
    refetchOnWindowFocus: false,

    // Refetch on mount to ensure fresh data when modal opens
    refetchOnMount: true,

    // Keep previous data while refetching to prevent loading flicker
    placeholderData: (previousData) => previousData,

    // Retry once on failure
    retry: 1,
    retryDelay: 1000,
  });
}

/**
 * Fetch eligible bookings (ready for payout)
 *
 * Cached for 1 minute since this data is more time-sensitive
 * Invalidated when payout is requested
 *
 * @example
 * ```typescript
 * const { data: eligibleBookings, isLoading } = useEligibleBookings(venueId);
 * ```
 */
export function useEligibleBookings(venueId: number | undefined) {
  return useQuery<PayoutBooking[], Error>({
    queryKey: ['eligible-bookings', venueId],
    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');
      return vendorApi.getEligibleBookings(venueId);
    },
    enabled: !!venueId,

    // Cache for 1 minute (more time-sensitive than held bookings)
    staleTime: 1 * 60 * 1000,

    // Don't refetch on window focus
    refetchOnWindowFocus: false,

    // Refetch on mount
    refetchOnMount: true,

    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,

    retry: 1,
    retryDelay: 1000,
  });
}

/**
 * Fetch reserved bookings (future bookings)
 *
 * Cached for 3 minutes since future bookings change less frequently
 *
 * @example
 * ```typescript
 * const { data: reservedBookings, isLoading } = useReservedBookings(venueId);
 * ```
 *
 * @todo Implement getReservedBookings API method in vendorApi
 */
export function useReservedBookings(venueId: number | undefined) {
  return useQuery<PayoutBooking[], Error>({
    queryKey: ['reserved-bookings', venueId],
    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // TODO: Implement this API endpoint in backend and frontend
      // const data = await vendorApi.getReservedBookings(venueId);
      // For now, return empty array
      return [];
    },
    enabled: false, // Disabled until API is implemented

    // Cache for 3 minutes (future bookings are less volatile)
    staleTime: 3 * 60 * 1000,

    // Don't refetch on window focus
    refetchOnWindowFocus: false,

    // Refetch on mount
    refetchOnMount: true,

    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,

    retry: 1,
    retryDelay: 1000,
  });
}

/**
 * Type guard to check if bookings data is loaded
 */
export function hasBookingsData(
  data: PayoutBooking[] | undefined
): data is PayoutBooking[] {
  return Array.isArray(data);
}

/**
 * Helper to count bookings by type
 */
export function countBookingsByType(bookings: PayoutBooking[] | undefined) {
  if (!hasBookingsData(bookings)) return { oneTime: 0, recurring: 0, membership: 0 };

  return bookings.reduce(
    (acc, booking) => {
      const type = booking.booking_type || 'one_time';
      if (type === 'one_time') acc.oneTime++;
      else if (type === 'recurring') acc.recurring++;
      else if (type === 'membership') acc.membership++;
      return acc;
    },
    { oneTime: 0, recurring: 0, membership: 0 }
  );
}

/**
 * Helper to sum total vendor share from bookings
 */
export function sumVendorShare(bookings: PayoutBooking[] | undefined): number {
  if (!hasBookingsData(bookings)) return 0;

  return bookings.reduce((sum, booking) => {
    // Try both field names (vendor_share or venue_share)
    const share = booking.vendor_share || booking.venue_share || 0;
    return sum + (typeof share === 'string' ? parseFloat(share) : share);
  }, 0);
}

/**
 * Interface for reserved refunds (bookings with pending refund requests)
 */
export interface ReservedRefund {
  id: number;
  reserved_amount: number;
  reason: string;
  refund_request_id: number;
  booking_reference: string;
  customer_name: string;
  requested_amount: number;
  refund_status: string;
  created_at: string;
  booking_date: string | null;
}

export interface ReservedRefundsData {
  data: ReservedRefund[];
  total_reserved: number;
}

/**
 * Fetch reserved refunds (bookings with pending refund requests)
 *
 * Cached for 1 minute since refunds change when processed
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useReservedRefunds(venueId);
 * const refunds = data?.data || [];
 * const total = data?.total_reserved || 0;
 * ```
 */
export function useReservedRefunds(venueId: number | undefined) {
  return useQuery<ReservedRefundsData, Error>({
    queryKey: ['reserved-refunds', venueId],
    queryFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');

      // Use vendorApi's private api instance to make authenticated request
      // We'll need to add this method to vendorApi
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/vendor/venues/${venueId}/bookings/reserved-refunds`,
        {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('vendor_token') || ''}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch reserved refunds');
      }

      const result = await response.json();
      return result;
    },
    enabled: !!venueId,

    // Cache for 1 minute
    staleTime: 1 * 60 * 1000,

    // Don't refetch on window focus
    refetchOnWindowFocus: false,

    // Refetch on mount
    refetchOnMount: true,

    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,

    retry: 1,
    retryDelay: 1000,
  });
}
